import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, TextField, FormControlLabel, Switch, Button, Stack, Typography } from '@mui/material';
import { IuguAmbiente, IuguConfig, IuguConfigInput } from '../../../services/iuguConfigService';

// Helpers for validation
const isValidUrl = (val: string) => {
  try {
    const u = new URL(val);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const hasUpTo4Decimals = (s: string) => {
  if (!s.includes('.')) return true;
  const [, dec] = s.split('.');
  return dec.length <= 4;
};

// Form schema (string-based for numeric inputs to allow empty -> "")
const formSchema = z.object({
  accountId: z.string().optional(),
  webhookUrl: z.string().optional().refine((v) => !v || isValidUrl(v), 'URL inválida'),
  enableCreditCard: z.boolean().optional(),
  enablePix: z.boolean().optional(),
  enableBankSlip: z.boolean().optional(),
  invoicePrefix: z.string().max(32, 'Máximo 32 caracteres').optional(),
  defaultDueDays: z
    .string()
    .optional()
    .refine((v) => v === undefined || v === '' || (/^\d+$/.test(v) && parseInt(v, 10) >= 0), 'Informe inteiro ≥ 0'),
  lateFinePercent: z
    .string()
    .optional()
    .refine(
      (v) => v === undefined || v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100 && hasUpTo4Decimals(v)),
      '0.0–100.0, até 4 casas decimais',
    ),
  monthlyInterestPercent: z
    .string()
    .optional()
    .refine(
      (v) => v === undefined || v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100 && hasUpTo4Decimals(v)),
      '0.0–100.0, até 4 casas decimais',
    ),
  notificationUrl: z.string().optional().refine((v) => !v || isValidUrl(v), 'URL inválida'),
  apiToken: z.string().optional(),
  webhookSecret: z.string().optional(),
});

export type IuguFormValues = z.infer<typeof formSchema>;

export interface IuguConfigFormProps {
  title: string;
  env: IuguAmbiente;
  initialData?: IuguConfig | null;
  onSubmit: (data: IuguConfigInput, id?: string) => Promise<IuguConfig>;
  onAfterSave?: (config: IuguConfig) => void;
  activateButton?: React.ReactNode;
}

const IuguConfigForm: React.FC<IuguConfigFormProps> = ({ title, env, initialData, onSubmit, onAfterSave, activateButton }) => {
  const { handleSubmit, control, formState: { errors, isSubmitting }, reset } = useForm<IuguFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountId: initialData?.accountId ?? '',
      webhookUrl: initialData?.webhookUrl ?? '',
      enableCreditCard: initialData?.enableCreditCard ?? false,
      enablePix: initialData?.enablePix ?? false,
      enableBankSlip: initialData?.enableBankSlip ?? false,
      invoicePrefix: initialData?.invoicePrefix ?? '',
      defaultDueDays: initialData?.defaultDueDays == null ? '' : String(initialData.defaultDueDays),
      lateFinePercent: initialData?.lateFinePercent == null ? '' : String(initialData.lateFinePercent),
      monthlyInterestPercent: initialData?.monthlyInterestPercent == null ? '' : String(initialData.monthlyInterestPercent),
      notificationUrl: initialData?.notificationUrl ?? '',
      apiToken: '',
      webhookSecret: '',
    },
  });

  const submitHandler = async (values: IuguFormValues) => {
    const input: IuguConfigInput = {
      ambiente: env,
      accountId: values.accountId && values.accountId.trim() !== '' ? values.accountId : null,
      webhookUrl: values.webhookUrl && values.webhookUrl.trim() !== '' ? values.webhookUrl : null,
      enableCreditCard: Boolean(values.enableCreditCard),
      enablePix: Boolean(values.enablePix),
      enableBankSlip: Boolean(values.enableBankSlip),
      invoicePrefix: values.invoicePrefix && values.invoicePrefix.trim() !== '' ? values.invoicePrefix : null,
      defaultDueDays: values.defaultDueDays && values.defaultDueDays !== '' ? parseInt(values.defaultDueDays, 10) : null,
      lateFinePercent: values.lateFinePercent && values.lateFinePercent !== '' ? Number(values.lateFinePercent) : null,
      monthlyInterestPercent: values.monthlyInterestPercent && values.monthlyInterestPercent !== '' ? Number(values.monthlyInterestPercent) : null,
      notificationUrl: values.notificationUrl && values.notificationUrl.trim() !== '' ? values.notificationUrl : null,
      apiToken: values.apiToken && values.apiToken.trim() !== '' ? values.apiToken : null,
      webhookSecret: values.webhookSecret && values.webhookSecret.trim() !== '' ? values.webhookSecret : null,
    };

    const saved = await onSubmit(input, initialData?.id);
    reset({ ...values, apiToken: '', webhookSecret: '' });
    onAfterSave?.(saved);
  };

  return (
    <Card variant="outlined">
      <CardHeader title={title} action={activateButton} sx={{ pb: 0 }} />
      <CardContent>
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Account ID" fullWidth error={!!errors.accountId} helperText={errors.accountId?.message as string} />
                )}
              />
            </div>
            <div className="col-12 col-md-6">
              <Controller
                name="webhookUrl"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Webhook URL" fullWidth error={!!errors.webhookUrl} helperText={errors.webhookUrl?.message as string} />
                )}
              />
            </div>

            <div className="col-12 col-md-4">
              <Controller
                name="enableCreditCard"
                control={control}
                render={({ field }) => (
                  <FormControlLabel control={<Switch {...field} checked={!!field.value} />} label="Habilitar Cartão" />
                )}
              />
            </div>
            <div className="col-12 col-md-4">
              <Controller
                name="enablePix"
                control={control}
                render={({ field }) => (
                  <FormControlLabel control={<Switch {...field} checked={!!field.value} />} label="Habilitar Pix" />
                )}
              />
            </div>
            

            <div className="col-12 col-md-6">
              <Controller
                name="invoicePrefix"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Prefixo da fatura" fullWidth inputProps={{ maxLength: 32 }} error={!!errors.invoicePrefix} helperText={errors.invoicePrefix?.message as string} />
                )}
              />
            </div>

            <div className="col-12 col-md-6">
              <Controller
                name="defaultDueDays"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Vencimento padrão (dias)" fullWidth type="number" inputProps={{ min: 0 }} error={!!errors.defaultDueDays} helperText={errors.defaultDueDays?.message as string} />
                )}
              />
            </div>

            <div className="col-12 col-md-6">
              <Controller
                name="lateFinePercent"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Multa por atraso (%)" fullWidth type="number" inputProps={{ min: 0, max: 100, step: 0.0001 }} error={!!errors.lateFinePercent} helperText={errors.lateFinePercent?.message as string} />
                )}
              />
            </div>

            <div className="col-12 col-md-6">
              <Controller
                name="monthlyInterestPercent"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Juros mensal (%)" fullWidth type="number" inputProps={{ min: 0, max: 100, step: 0.0001 }} error={!!errors.monthlyInterestPercent} helperText={errors.monthlyInterestPercent?.message as string} />
                )}
              />
            </div>

            <div className="col-12">
              <Controller
                name="notificationUrl"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Notification URL" fullWidth error={!!errors.notificationUrl} helperText={errors.notificationUrl?.message as string} />
                )}
              />
            </div>

            <div className="col-12 col-md-6">
              <Controller
                name="apiToken"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="API Token" type="password" placeholder="************" fullWidth helperText={initialData?.apiTokenMasked ? `Configurado: ${initialData.apiTokenMasked}` : undefined} />
                )}
              />
            </div>
            <div className="col-12 col-md-6">
              <Controller
                name="webhookSecret"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Webhook Secret" type="password" placeholder="************" fullWidth helperText={initialData?.webhookSecretMasked ? `Configurado: ${initialData.webhookSecretMasked}` : undefined} />
                )}
              />
            </div>
          </div>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {env === 'TESTE' ? 'Ambiente de Homologação' : 'Ambiente de Produção'}
            </Typography>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? (env === 'TESTE' ? 'Salvando Homologação...' : 'Salvando Produção...') : env === 'TESTE' ? 'Salvar Homologação' : 'Salvar Produção'}
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
};

export default IuguConfigForm;

